"use client";

import { useCallback, useMemo, useState } from "react";

import type { AssessmentUpdatePayload } from "@/lib/websocket/notification-service";

import {
  useRealtimeChannel,
  type RealtimeOrigin,
} from "./use-realtime-channel";

export interface AssessmentUpdateMessage extends AssessmentUpdatePayload {
  userId: string;
  updatedAt: string;
  origin?: RealtimeOrigin;
  clientId?: string;
}

interface UseAssessmentSyncOptions {
  userId?: string | null;
  enabled?: boolean;
  onUpdate?: (payload: AssessmentUpdateMessage) => void;
}

export function useAssessmentSync({
  userId,
  enabled = true,
  onUpdate,
}: UseAssessmentSyncOptions = {}) {
  const [lastUpdate, setLastUpdate] = useState<AssessmentUpdateMessage | null>(
    null,
  );

  const handleMessage = useCallback(
    (payload: AssessmentUpdateMessage) => {
      setLastUpdate(payload);
      onUpdate?.(payload);
    },
    [onUpdate],
  );

  const channelName = useMemo(
    () =>
      userId ? `talkmaster:assessment:${userId}` : "talkmaster:assessment",
    [userId],
  );

  useRealtimeChannel<AssessmentUpdateMessage>({
    userId,
    enabled,
    channelName,
    socketEvent: "assessment:updated",
    onMessage: handleMessage,
  });

  return {
    lastUpdate,
  };
}
